import {
  IconButton,
  PrimaryButton,
  SelectionMode,
  Stack,
  Text,
} from '@fluentui/react';
import { useCommonStyles } from 'common/styles';
import { HelpIcon } from 'common/trainingSystem/HelpIcon';
import DATA_ACCESS_POLICY_LIST from './DataAccessPolicies.graphql';
import { NetworkStatus, useQuery } from '@apollo/client';
import {
  DataAccessPolicies,
  DataAccessPolicies_dataAccessPolicies_nodes,
  DataAccessPoliciesVariables,
} from './__generated__/DataAccessPolicies';
import { TABLE_ROWS } from 'common/constants';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ColumnStylesProps, onRenderItems } from './columns.render';
import { useStyles } from './index.styles';
import { InfiniteList } from 'common/components/InfiniteList';
import { ColumnData } from 'common/components/SearchBar';
import { getColumns } from './column.data';
import { onHandleSearch } from './utils';
import { OrderDirection, SortOrder } from 'common/utils/commonTypes';
import { getSortedColumns } from 'common/utils/columnUtilities';
import { FormModal } from '../view/FormModal';

export type DataAccessPolicyRow = DataAccessPolicies_dataAccessPolicies_nodes;

export const DataAccessPolicyList: React.FC = () => {
  const commonStyles = useCommonStyles();
  const renderRef = useRef(false);
  const styles = useStyles();
  const [sortOrderParam, setSortOrderParam] = useState<SortOrder>();
  const [gridColumns, setGridColumns] = useState<ColumnData[]>([]);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);

  const columnStyles: ColumnStylesProps = {
    onRenderColumnStack: styles.onRenderColumnStack,
    dataAccessPolicyCol: styles.dataAccessPolicyCol,
  };
  const {
    loading: dataAccessPoliciesLoading,
    data: dataAccessPoliciesListData,
    fetchMore,
    networkStatus,
    refetch,
  } = useQuery<DataAccessPolicies, DataAccessPoliciesVariables>(
    DATA_ACCESS_POLICY_LIST,
    {
      variables: {
        first: TABLE_ROWS,
        orderBy: ['NAME_ASC', 'PRIMARY_KEY_ASC'],
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    }
  );
  const handleSearch = async (showMore: boolean) => {
    const { queryVariables } = onHandleSearch(
      showMore,
      dataAccessPoliciesListData,
      sortOrderParam
    );
    if (showMore) await fetchMore?.({ variables: queryVariables });
    else refetch(queryVariables);
  };

  const handleSearchMemo = useCallback(handleSearch, [sortOrderParam]);

  useEffect(() => {
    if (renderRef.current) {
      handleSearchMemo(false);
    } else renderRef.current = true;
  }, [handleSearchMemo]);

  const _onColumnClick = (clickedColumn: ColumnData) => {
    const { newColumns, desc } = getSortedColumns(clickedColumn, gridColumns);
    setGridColumns(newColumns);
    setSortOrderParam({
      column: clickedColumn.key,
      direction: desc ? OrderDirection.DESC : OrderDirection.ASC,
    });
  };

  const refetching =
    networkStatus === NetworkStatus.refetch ||
    networkStatus === NetworkStatus.setVariables ||
    networkStatus === NetworkStatus.loading;
  const { nodes } = {
    ...dataAccessPoliciesListData?.dataAccessPolicies,
  };

  const transformedData = refetching ? undefined : nodes || [];

  const _renderItemColumn = useCallback(onRenderItems(columnStyles), [
    columnStyles,
  ]);
  const setCustomColumns = useCallback(() => {
    setGridColumns(getColumns());
  }, []);

  useEffect(() => {
    setCustomColumns();
  }, [setCustomColumns]);

  return (
    <>
      <Stack
        tokens={{ childrenGap: 20 }}
        className={commonStyles.listHeaderContainer}
      >
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
          className={commonStyles.listTitleContainer}
        >
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 7 }}>
            <Text variant="xLarge">Data Access Policies</Text>
            <HelpIcon appLinkType="DATA_ACCESS_POLICIES" />
          </Stack>
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <IconButton
              iconProps={{ iconName: 'refresh' }}
              onClick={() => handleSearch(false)}
            />
            <PrimaryButton
              iconProps={{
                iconName: 'Add',
              }}
              text="New Data Access Policy"
              onClick={() => setIsViewModalOpen(true)}
            />
            {isViewModalOpen && (
              <FormModal onClose={() => setIsViewModalOpen(false)} />
            )}
          </Stack>
        </Stack>
      </Stack>
      <InfiniteList
        loading={dataAccessPoliciesLoading}
        onRenderItemColumn={_renderItemColumn}
        hasNextPage={
          dataAccessPoliciesListData?.dataAccessPolicies?.pageInfo.hasNextPage
        }
        items={transformedData}
        selectionMode={SelectionMode.none}
        columns={gridColumns.filter((_column) => _column.isVisible)}
        onColumnHeaderClick={(_, column) => {
          if (column) _onColumnClick(column);
        }}
        onLoadMore={async () => await handleSearch(true)}
      />
    </>
  );
};
